import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';

const DARK_BLUE = '#0A1929';
const GREEN = '#4caf50';

interface MarkdownPreviewProps {
  content: string;
  maxHeight?: number | string;
}

/**
 * Renders a Markdown string as styled HTML.
 * Supports GFM tables, headings, bold, italic, lists, blockquotes, code blocks, and horizontal rules.
 */
const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({ content, maxHeight }) => (
  <Box
    sx={{
      overflowY: maxHeight ? 'auto' : undefined,
      maxHeight: maxHeight ?? undefined,
      '& h1': { fontSize: '1.4rem', fontWeight: 700, mt: 3, mb: 1 },
      '& h2': { fontSize: '1.2rem', fontWeight: 700, mt: 2.5, mb: 1 },
      '& h3': { fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 0.5 },
      '& p': { fontSize: '0.875rem', lineHeight: 1.7, mb: 1 },
      '& ul, & ol': { pl: 3, mb: 1, '& li': { fontSize: '0.875rem', lineHeight: 1.7, mb: 0.3 } },
      '& blockquote': {
        borderLeft: `4px solid ${GREEN}`,
        pl: 2,
        py: 0.5,
        my: 1,
        bgcolor: alpha(GREEN, 0.04),
        borderRadius: '0 8px 8px 0',
        '& p': { fontStyle: 'italic', color: 'text.secondary', mb: 0 },
      },
      '& hr': { border: 'none', borderTop: `1px solid ${alpha(DARK_BLUE, 0.12)}`, my: 2 },
      '& code': {
        fontFamily: 'monospace',
        fontSize: '0.82rem',
        bgcolor: alpha(DARK_BLUE, 0.06),
        px: 0.5,
        borderRadius: 0.5,
      },
      '& pre': {
        bgcolor: DARK_BLUE,
        color: '#e0e0e0',
        p: 2,
        borderRadius: 2,
        overflow: 'auto',
        my: 1.5,
        '& code': { bgcolor: 'transparent', px: 0 },
      },
      '& table': {
        width: '100%',
        borderCollapse: 'collapse',
        my: 1.5,
        fontSize: '0.82rem',
      },
      '& th': {
        fontWeight: 700,
        textAlign: 'left',
        borderBottom: `2px solid ${alpha(DARK_BLUE, 0.2)}`,
        px: 1.5,
        py: 1,
        bgcolor: alpha(DARK_BLUE, 0.04),
        whiteSpace: 'nowrap',
      },
      '& td': {
        borderBottom: `1px solid ${alpha(DARK_BLUE, 0.08)}`,
        px: 1.5,
        py: 0.75,
      },
      '& tr:last-child td': { borderBottom: 'none' },
      '& strong': { fontWeight: 700 },
      '& a': { color: GREEN, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
    }}
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
  </Box>
);

export default MarkdownPreview;
